import { FormFieldInputType } from 'models/enum';
import { ChartMetaFormField } from 'models/ui';

export enum FormField {
  // Step 1 — Patient Information & Medical History
  PATIENT_NAME = 'patientName',
  PATIENT_AGE = 'patientAge',
  PATIENT_SEX = 'patientSex',
  SPOKESPERSON = 'spokesperson',
  RELATIONSHIP_TO_PATIENT = 'relationshipToPatient',
  PREFERRED_LANGUAGE = 'preferredLanguage',
  INTERPRETER_OFFERED = 'interpreterOffered',
  NAME_OF_INTERPRETER = 'nameOfInterpreter',

  // Step 1 — Allergies modal working fields
  ALLERGEN_TYPE = 'allergenType',
  ALLERGEN_TYPE_OTHER = 'allergenTypeOther',
  SPECIFIC_ALLERGEN = 'specificAllergen',
  SPECIFIC_ALLERGEN_OTHER = 'specificAllergenOther',
  ALLERGY_REACTION = 'allergyReaction',
  ALLERGY_REACTION_OTHER = 'allergyReactionOther',
  ALLERGY_SEVERITY = 'allergySeverity',

  // Step 1 — Surgeries modal working fields
  SURGERY_YEAR = 'surgeryYear',
  SURGERY_PROCEDURE = 'surgeryProcedure',

  // Step 1 — Medical Problems / Chronic Diseases modal working fields
  MEDICAL_PROBLEM_YEAR = 'medicalProblemYearDiagnosed',
  MEDICAL_PROBLEM_DISEASE = 'medicalProblemDiseaseCondition',

  // Step 2 — Assessments
  MENTAL_STATUS = 'mentalStatus',
  MENTAL_STATUS_OTHER = 'mentalStatusOther',
  ABUSE_FEEL_SAFE = 'abuseFeelSafe',
  ABUSE_EXPLOITED = 'abuseExploited',
  ABUSE_DISCUSS_SITUATION = 'abuseDiscussSituation',
  ABUSE_PLANS_FOR_REFERRAL = 'abusePlansForReferral',
  FUNCTIONAL_ASSESSMENT = 'functionalAssessment',
  FUNCTIONAL_NEEDS_REFERRAL = 'functionalNeedsReferral',
  FUNCTIONAL_REFERRED_TO = 'functionalReferredTo',
  FUNCTIONAL_CARE_ARRANGEMENTS = 'functionalCareArrangements',
  LEARNING_HOW_LEARN_BEST = 'learningHowLearnBest',
  LEARNING_BARRIERS = 'learningBarriers',
  LEARNING_SPECIFIC_NEEDS = 'learningSpecificNeeds',
  VALUE_BELIEFS_CONFLICT = 'valueBeliefsConflict',
  VALUE_DESCRIBE_BELIEFS = 'valueDescribeBeliefs',

  // Step 3 — Immunizations (status / date / desire booster per vaccine)
  IMM_TDAP_STATUS = 'immTdapStatus',
  IMM_TDAP_DATE = 'immTdapDate',
  IMM_TDAP_BOOSTER = 'immTdapBooster',
  IMM_HPV_STATUS = 'immHpvStatus',
  IMM_HPV_DATE = 'immHpvDate',
  IMM_HPV_BOOSTER = 'immHpvBooster',
  IMM_ZOSTER_STATUS = 'immZosterStatus',
  IMM_ZOSTER_DATE = 'immZosterDate',
  IMM_ZOSTER_BOOSTER = 'immZosterBooster',
  IMM_VARICELLA_STATUS = 'immVaricellaStatus',
  IMM_VARICELLA_DATE = 'immVaricellaDate',
  IMM_VARICELLA_BOOSTER = 'immVaricellaBooster',
  IMM_MMR_STATUS = 'immMmrStatus',
  IMM_MMR_DATE = 'immMmrDate',
  IMM_MMR_BOOSTER = 'immMmrBooster',
  IMM_INFLUENZA_STATUS = 'immInfluenzaStatus',
  IMM_INFLUENZA_DATE = 'immInfluenzaDate',
  IMM_INFLUENZA_BOOSTER = 'immInfluenzaBooster',
  IMM_PNEUMOCOCCAL_STATUS = 'immPneumococcalStatus',
  IMM_PNEUMOCOCCAL_DATE = 'immPneumococcalDate',
  IMM_PNEUMOCOCCAL_BOOSTER = 'immPneumococcalBooster',
  IMM_HEP_A_STATUS = 'immHepAStatus',
  IMM_HEP_A_DATE = 'immHepADate',
  IMM_HEP_A_BOOSTER = 'immHepABooster',
  IMM_HEP_B_STATUS = 'immHepBStatus',
  IMM_HEP_B_DATE = 'immHepBDate',
  IMM_HEP_B_BOOSTER = 'immHepBBooster',
  IMM_MENINGOCOCCAL_STATUS = 'immMeningococcalStatus',
  IMM_MENINGOCOCCAL_DATE = 'immMeningococcalDate',
  IMM_MENINGOCOCCAL_BOOSTER = 'immMeningococcalBooster',
  IMM_OTHER_IMMUNIZATION = 'immOtherImmunization',
  IMM_OTHER_STATUS = 'immOtherStatus',
  IMM_OTHER_DATE = 'immOtherDate',
  IMM_OTHER_BOOSTER = 'immOtherBooster',

  // Step 4 — Living Arrangements, Support System & Health Goals
  LIVING_ARRANGEMENTS = 'livingArrangements',
  LIVING_SATISFIED_SAFETY = 'livingSatisfiedSafety',
  LIVING_SUPPORT_RELY_ON = 'livingSupportRelyOn',
  LIVING_NEIGHBORS_FAMILY = 'livingNeighborsFamily',
  // Yearly Healthy Goals modal working fields
  GOAL = 'goal',
  GOAL_PLAN = 'goalPlan',
  EXAMINING_PROVIDER = 'examiningProvider',
  EXAMINING_TIME = 'examiningTime'
}

export enum SectionTitle {
  ANNUAL_AMBULATORY_CARE = 'Annual Ambulatory Care'
}

export enum ViewTitle {
  ANNUAL_AMBULATORY_CARE_RECORD = 'Annual Ambulatory Care'
}

export interface AnnualAmbulatoryFieldConfig {
  name: FormField;
  label: string;
  type: FormFieldInputType;
  displayHorizontal?: boolean;
  noWrapLabel?: boolean;
  maxLength?: number;
  showCharCount?: boolean;
  customSelect?: boolean;
}

export interface AnnualAmbulatorySectionConfig {
  title: string;
  rows: FormField[][];
  fixedWidth?: boolean;
  width?: string;
  lightDivider?: boolean;
  collections?: CollectionId[];
}

export interface AnnualAmbulatoryStepConfig {
  title: string;
  sections: AnnualAmbulatorySectionConfig[];
}

export enum CollectionId {
  ALLERGIES = 'allergies',
  SURGERIES = 'surgeries',
  MEDICAL_PROBLEMS = 'medicalProblems',
  YEARLY_GOALS = 'yearlyGoals'
}

export interface CollectionColumn {
  field: FormField;
  header: string;
  type?: FormFieldInputType;
  otherField?: FormField;
  dependsOn?: FormField;
  disabledWhen?: { field: FormField; value: string };
}

export interface CollectionConfig {
  id: CollectionId;
  title: string;
  modalTitle: string;
  columns: CollectionColumn[];
  inlineEntry?: boolean;
}

export type CollectionRow = Record<string, string>;

export const CHARACTER_LIMIT = 150;

const withTextInputMaxLength = (fieldConfig: AnnualAmbulatoryFieldConfig): AnnualAmbulatoryFieldConfig =>
  fieldConfig.type === FormFieldInputType.TEXT_BOX ? { ...fieldConfig, maxLength: fieldConfig.maxLength ?? CHARACTER_LIMIT } : fieldConfig;

export const characterCountText = (value: unknown, maxLength: number): string => `${String(value ?? '').length}/${maxLength} characters`;

export const hasFieldValue = (formField: ChartMetaFormField | undefined): boolean => {
  if (!formField) return false;
  const { value } = formField;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'number') return true;
  return String(value ?? '').trim().length > 0;
};

export interface VaccineConfig {
  label: string;
  statusField: FormField;
  dateField: FormField;
  boosterField: FormField;
}

export const VACCINE_CONFIGS: VaccineConfig[] = [
  {
    label: 'Tetanus, diphtheria, pertussis (Td/Tdap):',
    statusField: FormField.IMM_TDAP_STATUS,
    dateField: FormField.IMM_TDAP_DATE,
    boosterField: FormField.IMM_TDAP_BOOSTER
  },
  { label: 'Human papillomavirus (HPV):', statusField: FormField.IMM_HPV_STATUS, dateField: FormField.IMM_HPV_DATE, boosterField: FormField.IMM_HPV_BOOSTER },
  { label: 'Zoster:', statusField: FormField.IMM_ZOSTER_STATUS, dateField: FormField.IMM_ZOSTER_DATE, boosterField: FormField.IMM_ZOSTER_BOOSTER },
  { label: 'Varicella:', statusField: FormField.IMM_VARICELLA_STATUS, dateField: FormField.IMM_VARICELLA_DATE, boosterField: FormField.IMM_VARICELLA_BOOSTER },
  { label: 'Measles, mumps, rubella (MMR):', statusField: FormField.IMM_MMR_STATUS, dateField: FormField.IMM_MMR_DATE, boosterField: FormField.IMM_MMR_BOOSTER },
  { label: 'Influenza:', statusField: FormField.IMM_INFLUENZA_STATUS, dateField: FormField.IMM_INFLUENZA_DATE, boosterField: FormField.IMM_INFLUENZA_BOOSTER },
  {
    label: 'Pneumococcal (polysaccharide):',
    statusField: FormField.IMM_PNEUMOCOCCAL_STATUS,
    dateField: FormField.IMM_PNEUMOCOCCAL_DATE,
    boosterField: FormField.IMM_PNEUMOCOCCAL_BOOSTER
  },
  { label: 'Hepatitis A:', statusField: FormField.IMM_HEP_A_STATUS, dateField: FormField.IMM_HEP_A_DATE, boosterField: FormField.IMM_HEP_A_BOOSTER },
  { label: 'Hepatitis B:', statusField: FormField.IMM_HEP_B_STATUS, dateField: FormField.IMM_HEP_B_DATE, boosterField: FormField.IMM_HEP_B_BOOSTER },
  { label: 'Meningococcal:', statusField: FormField.IMM_MENINGOCOCCAL_STATUS, dateField: FormField.IMM_MENINGOCOCCAL_DATE, boosterField: FormField.IMM_MENINGOCOCCAL_BOOSTER }
];

const buildImmunizationFieldConfigs = (): AnnualAmbulatoryFieldConfig[] => {
  const fields: AnnualAmbulatoryFieldConfig[] = [];
  VACCINE_CONFIGS.forEach((vaccine) => {
    fields.push({ name: vaccine.statusField, label: 'Status', type: FormFieldInputType.DROPDOWN });
    fields.push({ name: vaccine.dateField, label: 'Date', type: FormFieldInputType.TEXT_BOX });
    fields.push({ name: vaccine.boosterField, label: 'Desire Booster', type: FormFieldInputType.DROPDOWN });
  });
  fields.push({ name: FormField.IMM_OTHER_IMMUNIZATION, label: 'Immunization', type: FormFieldInputType.TEXT_BOX });
  fields.push({ name: FormField.IMM_OTHER_DATE, label: 'Date', type: FormFieldInputType.TEXT_BOX });
  fields.push({ name: FormField.IMM_OTHER_BOOSTER, label: 'Desire Booster', type: FormFieldInputType.DROPDOWN });
  fields.push({ name: FormField.IMM_OTHER_STATUS, label: 'Status', type: FormFieldInputType.DROPDOWN });
  return fields;
};

export const FIELD_CONFIGS: AnnualAmbulatoryFieldConfig[] = [
  // Step 1
  { name: FormField.PATIENT_NAME, label: 'Patient Name', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.PATIENT_AGE, label: 'Patient Age', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.PATIENT_SEX, label: 'Patient Sex', type: FormFieldInputType.DROPDOWN, customSelect: true },
  { name: FormField.SPOKESPERSON, label: 'Spokesperson', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.RELATIONSHIP_TO_PATIENT, label: 'Relationship to Patient', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.PREFERRED_LANGUAGE, label: 'Preferred Language', type: FormFieldInputType.DROPDOWN, customSelect: true },
  { name: FormField.INTERPRETER_OFFERED, label: 'Interpreter Offered', type: FormFieldInputType.DROPDOWN, customSelect: true },
  { name: FormField.NAME_OF_INTERPRETER, label: 'Name of Interpreter', type: FormFieldInputType.TEXT_BOX },

  // Step 1 — Allergies modal working fields
  { name: FormField.ALLERGEN_TYPE, label: 'Allergen Type', type: FormFieldInputType.DROPDOWN },
  { name: FormField.ALLERGEN_TYPE_OTHER, label: 'Other', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.SPECIFIC_ALLERGEN, label: 'Specific Allergen', type: FormFieldInputType.MULTISELECT_DROPDOWN },
  { name: FormField.SPECIFIC_ALLERGEN_OTHER, label: 'Other', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.ALLERGY_REACTION, label: 'Reaction', type: FormFieldInputType.MULTISELECT_DROPDOWN },
  { name: FormField.ALLERGY_REACTION_OTHER, label: 'Other', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.ALLERGY_SEVERITY, label: 'Severity', type: FormFieldInputType.DROPDOWN },

  // Step 1 — Surgeries modal working fields
  { name: FormField.SURGERY_YEAR, label: 'Performed', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.SURGERY_PROCEDURE, label: 'Procedure', type: FormFieldInputType.TEXT_BOX },

  // Step 1 — Medical Problems modal working fields
  { name: FormField.MEDICAL_PROBLEM_YEAR, label: 'Diagnosed', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.MEDICAL_PROBLEM_DISEASE, label: 'Disease/Condition', type: FormFieldInputType.TEXT_BOX },

  // Step 2
  { name: FormField.MENTAL_STATUS, label: 'Mental Status', type: FormFieldInputType.MULTISELECT_DROPDOWN },
  { name: FormField.MENTAL_STATUS_OTHER, label: 'Other', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.ABUSE_FEEL_SAFE, label: 'Do you feel safe at home and work?', type: FormFieldInputType.RADIO_CHOICE, displayHorizontal: true },
  {
    name: FormField.ABUSE_EXPLOITED,
    label: 'Have you been physically, verbally, emotionally, sexually, or\nfinancially exploited by someone within the past year?',
    type: FormFieldInputType.RADIO_CHOICE,
    displayHorizontal: true
  },
  {
    name: FormField.ABUSE_DISCUSS_SITUATION,
    label: 'Would you like to discuss your situation with a member of our staff?',
    type: FormFieldInputType.RADIO_CHOICE,
    displayHorizontal: true
  },
  {
    name: FormField.ABUSE_PLANS_FOR_REFERRAL,
    label: 'If Screen Indicates at-risk Situation, Describe Plans for Referral:',
    type: FormFieldInputType.TEXT_AREA,
    maxLength: 150,
    showCharCount: true
  },
  { name: FormField.FUNCTIONAL_ASSESSMENT, label: 'Assessment', type: FormFieldInputType.DROPDOWN, customSelect: true },
  { name: FormField.FUNCTIONAL_NEEDS_REFERRAL, label: 'Needs Referral for Assessment & Care', type: FormFieldInputType.DROPDOWN, customSelect: true },
  { name: FormField.FUNCTIONAL_REFERRED_TO, label: 'Referred to', type: FormFieldInputType.TEXT_BOX, showCharCount: true },
  {
    name: FormField.FUNCTIONAL_CARE_ARRANGEMENTS,
    label: 'If Needs Assistant or is Dependent, Describe Care Arrangements:',
    type: FormFieldInputType.TEXT_AREA,
    maxLength: 150,
    showCharCount: true
  },
  { name: FormField.LEARNING_HOW_LEARN_BEST, label: 'How do you learn best?', type: FormFieldInputType.TEXT_AREA, maxLength: 150, showCharCount: true },
  { name: FormField.LEARNING_BARRIERS, label: 'What do you see as barriers to learning?', type: FormFieldInputType.TEXT_AREA, maxLength: 150, showCharCount: true },
  { name: FormField.LEARNING_SPECIFIC_NEEDS, label: 'Do you have any specific learning needs?', type: FormFieldInputType.TEXT_AREA, maxLength: 150, showCharCount: true },
  {
    name: FormField.VALUE_BELIEFS_CONFLICT,
    label: 'Do you have any religious/cultural beliefs that might conflict with recommended health care/medical treatment?',
    type: FormFieldInputType.RADIO_CHOICE,
    displayHorizontal: true,
    noWrapLabel: true
  },
  { name: FormField.VALUE_DESCRIBE_BELIEFS, label: 'Describe Religious/Cultural Beliefs', type: FormFieldInputType.TEXT_BOX },

  // Step 3 — Immunizations
  ...buildImmunizationFieldConfigs(),

  // Step 4
  { name: FormField.LIVING_ARRANGEMENTS, label: 'What are your living arrangements?', type: FormFieldInputType.TEXT_AREA, maxLength: 150, showCharCount: true },
  {
    name: FormField.LIVING_SATISFIED_SAFETY,
    label: 'Are you satisfied with the safety and comfort of your living arrangements?',
    type: FormFieldInputType.TEXT_AREA,
    maxLength: 150,
    showCharCount: true
  },
  {
    name: FormField.LIVING_SUPPORT_RELY_ON,
    label: 'Who do you rely on for support and encouragement?',
    type: FormFieldInputType.TEXT_AREA,
    maxLength: 150,
    showCharCount: true
  },
  {
    name: FormField.LIVING_NEIGHBORS_FAMILY,
    label: 'Do you have neighbors and extended family who offer support?',
    type: FormFieldInputType.TEXT_AREA,
    maxLength: 150,
    showCharCount: true
  },
  { name: FormField.GOAL, label: 'Goals', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.GOAL_PLAN, label: 'Plans for Meeting Goals:', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.EXAMINING_PROVIDER, label: 'Examining Provider', type: FormFieldInputType.TEXT_BOX },
  { name: FormField.EXAMINING_TIME, label: 'Time', type: FormFieldInputType.TIME }
].map(withTextInputMaxLength);

// --- Collection configs -------------------------------------------------------

export const COLLECTION_CONFIGS: Record<CollectionId, CollectionConfig> = {
  [CollectionId.ALLERGIES]: {
    id: CollectionId.ALLERGIES,
    title: 'Allergies',
    modalTitle: 'Add Allergies',
    columns: [
      { field: FormField.ALLERGEN_TYPE, header: 'Allergen Type', type: FormFieldInputType.DROPDOWN, otherField: FormField.ALLERGEN_TYPE_OTHER },
      {
        field: FormField.SPECIFIC_ALLERGEN,
        header: 'Specific Allergen',
        type: FormFieldInputType.MULTISELECT_DROPDOWN,
        otherField: FormField.SPECIFIC_ALLERGEN_OTHER,
        dependsOn: FormField.ALLERGEN_TYPE
      },
      { field: FormField.ALLERGY_REACTION, header: 'Reaction', type: FormFieldInputType.MULTISELECT_DROPDOWN, otherField: FormField.ALLERGY_REACTION_OTHER },
      { field: FormField.ALLERGY_SEVERITY, header: 'Severity', type: FormFieldInputType.DROPDOWN }
    ]
  },
  [CollectionId.SURGERIES]: {
    id: CollectionId.SURGERIES,
    title: 'Surgeries',
    modalTitle: 'Add Surgeries',
    columns: [
      { field: FormField.SURGERY_YEAR, header: 'Performed', type: FormFieldInputType.TEXT_BOX },
      { field: FormField.SURGERY_PROCEDURE, header: 'Procedure', type: FormFieldInputType.TEXT_BOX }
    ]
  },
  [CollectionId.MEDICAL_PROBLEMS]: {
    id: CollectionId.MEDICAL_PROBLEMS,
    title: 'Medical Problems / Chronic Diseases',
    modalTitle: 'Add Medical Problems/Chronic Diseases',
    columns: [
      { field: FormField.MEDICAL_PROBLEM_YEAR, header: 'Diagnosed', type: FormFieldInputType.TEXT_BOX },
      { field: FormField.MEDICAL_PROBLEM_DISEASE, header: 'Disease/Condition', type: FormFieldInputType.TEXT_BOX }
    ]
  },
  [CollectionId.YEARLY_GOALS]: {
    id: CollectionId.YEARLY_GOALS,
    title: 'Yearly Healthy Goals',
    modalTitle: 'Add Yearly Healthy Goals',
    inlineEntry: true,
    columns: [
      { field: FormField.GOAL, header: 'Goals', type: FormFieldInputType.TEXT_BOX },
      { field: FormField.GOAL_PLAN, header: 'Plans for Meeting Goals:', type: FormFieldInputType.TEXT_BOX }
    ]
  }
};

/** Allergen Type → conditional Specific Allergen option metadata key (resolved from facade-config). */
export const ALLERGEN_TYPE_VALUES = {
  MEDICATION: 'Medication',
  FOOD: 'Food',
  ENVIRONMENTAL: 'Environmental',
  OTHER: 'Other'
};

export const OTHER_OPTION_VALUE = 'Other';

export const MULTI_VALUE_SEPARATOR = ', ';

export const formatOtherCellValue = (cellValue: string, otherValue: string): string => {
  const trimmedOther = String(otherValue ?? '').trim();
  if (!cellValue || !trimmedOther) return cellValue;
  return cellValue
    .split(MULTI_VALUE_SEPARATOR)
    .map((token) => (token.trim() === OTHER_OPTION_VALUE ? `${OTHER_OPTION_VALUE}: ${trimmedOther}` : token))
    .join(MULTI_VALUE_SEPARATOR);
};

export const SPECIFIC_ALLERGENS_BY_TYPE: Readonly<Record<string, readonly string[]>> = {
  [ALLERGEN_TYPE_VALUES.MEDICATION]: [
    'Amoxicillin',
    'Sulfa drugs',
    'Aspirin',
    'Ibuprofen (NSAIDs)',
    'Acetaminophen',
    'Codeine',
    'Morphine',
    'ACE inhibitors',
    'Local anesthetics',
    'Other'
  ],
  [ALLERGEN_TYPE_VALUES.FOOD]: ['Peanuts', 'Tree Nuts', 'Shellfish', 'Fish', 'Eggs', 'Milk (Dairy)', 'Soy', 'Wheat (Gluten)', 'Other'],
  [ALLERGEN_TYPE_VALUES.ENVIRONMENTAL]: ['Pollen', 'Dust Mites', 'Mold', 'Pet Dander (cats/dogs)', 'Grass', 'Other']
};

// --- Step layout --------------------------------------------------------------

export const STEP_CONFIGS: AnnualAmbulatoryStepConfig[] = [
  {
    title: 'Patient Information & Medical History',
    sections: [
      {
        title: 'Patient Information & Medical History',
        fixedWidth: true,
        rows: [
          [FormField.PATIENT_NAME, FormField.PATIENT_AGE, FormField.PATIENT_SEX],
          [FormField.SPOKESPERSON, FormField.RELATIONSHIP_TO_PATIENT]
        ]
      },
      {
        title: '',
        fixedWidth: true,
        rows: [[FormField.PREFERRED_LANGUAGE, FormField.INTERPRETER_OFFERED, FormField.NAME_OF_INTERPRETER]]
      },
      { title: '', rows: [], collections: [CollectionId.ALLERGIES] },
      { title: 'Significant Past Medical History', rows: [], collections: [CollectionId.SURGERIES] },
      { title: '', rows: [], lightDivider: true, collections: [CollectionId.MEDICAL_PROBLEMS] }
    ]
  },
  {
    title: 'Assessments',
    sections: [
      { title: 'Mental Assessment', width: '1o3', rows: [[FormField.MENTAL_STATUS], [FormField.MENTAL_STATUS_OTHER]] },
      {
        title: 'Abuse Assessment',
        lightDivider: true,
        rows: [[FormField.ABUSE_FEEL_SAFE], [FormField.ABUSE_EXPLOITED], [FormField.ABUSE_DISCUSS_SITUATION], [FormField.ABUSE_PLANS_FOR_REFERRAL]]
      },
      {
        title: 'Functional Assessment',
        lightDivider: true,
        rows: [[FormField.FUNCTIONAL_ASSESSMENT, FormField.FUNCTIONAL_NEEDS_REFERRAL, FormField.FUNCTIONAL_REFERRED_TO], [FormField.FUNCTIONAL_CARE_ARRANGEMENTS]]
      },
      {
        title: 'Learning Assessment',
        lightDivider: true,
        rows: [[FormField.LEARNING_HOW_LEARN_BEST, FormField.LEARNING_BARRIERS, FormField.LEARNING_SPECIFIC_NEEDS]]
      },
      {
        title: 'Value Assessment',
        lightDivider: true,
        rows: [[FormField.VALUE_BELIEFS_CONFLICT], [FormField.VALUE_DESCRIBE_BELIEFS]]
      }
    ]
  },
  {
    title: 'Immunizations',
    sections: [
      {
        title: 'Immunizations',
        rows: [
          ...VACCINE_CONFIGS.map((vaccine) => [vaccine.statusField, vaccine.dateField, vaccine.boosterField]),
          [FormField.IMM_OTHER_IMMUNIZATION, FormField.IMM_OTHER_DATE, FormField.IMM_OTHER_BOOSTER],
          [FormField.IMM_OTHER_STATUS]
        ]
      }
    ]
  },
  {
    title: 'Living Arrangements, Support System & Health Goals',
    sections: [
      {
        title: 'Living Arrangements',
        rows: [[FormField.LIVING_ARRANGEMENTS], [FormField.LIVING_SATISFIED_SAFETY], [FormField.LIVING_SUPPORT_RELY_ON], [FormField.LIVING_NEIGHBORS_FAMILY]]
      },
      {
        title: 'Yearly Healthy Goals',
        rows: [],
        lightDivider: true,
        collections: [CollectionId.YEARLY_GOALS]
      },
      {
        title: '',
        // Pinned to a third each so dropping the Date field does not stretch the two survivors.
        width: '1o3',
        rows: [[FormField.EXAMINING_PROVIDER, FormField.EXAMINING_TIME]],
        lightDivider: true
      }
    ]
  }
];

export const FIELD_CONFIG_MAP = new Map<FormField, AnnualAmbulatoryFieldConfig>(FIELD_CONFIGS.map((fieldConfig) => [fieldConfig.name, fieldConfig]));

export const COLLECTION_GROUP_TITLE = Object.fromEntries(
  STEP_CONFIGS.flatMap((step) => step.sections.flatMap((section) => (section.collections ?? []).map((collectionId) => [collectionId, step.title])))
) as Record<CollectionId, string>;

/** Fields revealed only while their controller holds a given value; no field is mandatory (SIMSNG-6062). */
export const CONDITIONAL_FIELDS: Partial<Record<FormField, { controller: FormField; whenValue: string }>> = {
  [FormField.VALUE_DESCRIBE_BELIEFS]: { controller: FormField.VALUE_BELIEFS_CONFLICT, whenValue: 'Yes' },
  [FormField.MENTAL_STATUS_OTHER]: { controller: FormField.MENTAL_STATUS, whenValue: OTHER_OPTION_VALUE },
  [FormField.NAME_OF_INTERPRETER]: { controller: FormField.INTERPRETER_OFFERED, whenValue: 'Yes' }
};

export const controllerSatisfies = (controllerValue: unknown, whenValue: string): boolean => {
  if (Array.isArray(controllerValue)) return controllerValue.map(String).includes(whenValue);
  return String(controllerValue ?? '')
    .split(/[|,]/)
    .map((part) => part.trim())
    .includes(whenValue);
};

export const controllerFieldSatisfies = (controllerField: ChartMetaFormField | undefined, whenValue: string): boolean => {
  const selectedInContent = controllerField?.chartContent?.some((item) => item.selected && (item.value === whenValue || item.name === whenValue));
  if (selectedInContent) return true;
  return controllerSatisfies(controllerField?.value, whenValue);
};
